// Engineering Worklist — design mock for the unified, trigger-aware redesign.
// (File still named EngineeringInboxMockPage.tsx; rename deferred until live wire-up.)
// Hardcoded mock data; not wired to any backend. Compare with /engineering/queue.
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  alpha,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as ChevronIcon,
  OpenInNew as OpenIcon,
  Snooze as SnoozeIcon,
  PersonAdd as AssignIcon,
  FiberManualRecord as DotIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────

type TriggerType = 'PCN' | 'EOL' | 'ALLOCATION' | 'OBSOLESCENCE' | 'RFQ' | 'NEW_BOM' | 'COST_DOWN';
type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
type GroupBy = 'PART' | 'BOM' | 'CUSTOMER';

interface AffectedBom {
  bomName: string;
  unitsPerQtr: number;
  stockWeeks: number;
  altStatus: 'APPROVED' | 'CANDIDATE' | 'NONE';
}

interface AffectedLine {
  line: number;
  partName: string;
  trigger: TriggerType;
  altStatus: 'APPROVED' | 'CANDIDATE' | 'NONE';
}

interface TriageItem {
  id: string;
  triggerType: TriggerType;
  subjectType: 'PART' | 'MATERIAL' | 'BOM' | 'CATEGORY';
  subjectName: string;
  subjectSubtitle?: string;
  severity: Severity;
  dueLabel?: string;
  revenueAtRisk?: string;
  bomsAffected?: number;
  linesAffected?: number;
  altsAvailable?: number;
  altsApproved?: number;
  sourceLabel: string;
  customer?: string;
  affectedBoms?: AffectedBom[];
  affectedLines?: AffectedLine[];
}

// ── Mock data ────────────────────────────────────────────────

const MOCK_ITEMS: TriageItem[] = [
  {
    id: 'tr-001',
    triggerType: 'PCN',
    subjectType: 'PART',
    subjectName: 'STM32F407VGT6',
    subjectSubtitle: 'STMicroelectronics · MCU 32-bit ARM Cortex-M4',
    severity: 'CRITICAL',
    dueLabel: 'LTB 94d',
    revenueAtRisk: '$4.2M',
    bomsAffected: 5,
    linesAffected: 12,
    altsAvailable: 3,
    altsApproved: 1,
    sourceLabel: 'PCN-ST-2026-0142',
    affectedBoms: [
      { bomName: 'BOM-Widget-A',  unitsPerQtr: 12000, stockWeeks: 3, altStatus: 'APPROVED' },
      { bomName: 'BOM-Widget-B',  unitsPerQtr: 3000,  stockWeeks: 8, altStatus: 'CANDIDATE' },
      { bomName: 'BOM-Gizmo-C',   unitsPerQtr: 800,   stockWeeks: 1, altStatus: 'NONE' },
      { bomName: 'BOM-Sensor-D',  unitsPerQtr: 4500,  stockWeeks: 5, altStatus: 'CANDIDATE' },
      { bomName: 'BOM-IoT-Hub-E', unitsPerQtr: 2200,  stockWeeks: 2, altStatus: 'CANDIDATE' },
    ],
  },
  {
    id: 'tr-002',
    triggerType: 'ALLOCATION',
    subjectType: 'CATEGORY',
    subjectName: 'MLCC 0402 X7R',
    subjectSubtitle: 'Multi-layer ceramic capacitor · 100nF / 16V',
    severity: 'HIGH',
    dueLabel: 'LT 12→32w',
    revenueAtRisk: '$9.1M',
    bomsAffected: 18,
    linesAffected: 47,
    altsAvailable: 6,
    altsApproved: 2,
    sourceLabel: 'MI signal · Murata allocation',
    affectedBoms: [
      { bomName: 'BOM-Widget-A',  unitsPerQtr: 12000, stockWeeks: 3, altStatus: 'APPROVED' },
      { bomName: 'BOM-Widget-B',  unitsPerQtr: 3000,  stockWeeks: 8, altStatus: 'CANDIDATE' },
      { bomName: 'BOM-Gizmo-C',   unitsPerQtr: 800,   stockWeeks: 1, altStatus: 'NONE' },
    ],
  },
  {
    id: 'tr-003',
    triggerType: 'OBSOLESCENCE',
    subjectType: 'MATERIAL',
    subjectName: 'AlSi12 Die-Cast Aluminum',
    subjectSubtitle: 'EN AC-44300 · superseded by AlSi10Mg',
    severity: 'MEDIUM',
    dueLabel: 'EOL 60d',
    revenueAtRisk: '$0.4M',
    bomsAffected: 2,
    linesAffected: 4,
    altsAvailable: 2,
    altsApproved: 0,
    sourceLabel: 'Lifecycle: ACTIVE → EOL',
    affectedBoms: [
      { bomName: 'BOM-Housing-X',   unitsPerQtr: 1500, stockWeeks: 12, altStatus: 'CANDIDATE' },
      { bomName: 'BOM-Bracket-Y',   unitsPerQtr: 600,  stockWeeks: 8,  altStatus: 'CANDIDATE' },
    ],
  },
  {
    id: 'tr-004',
    triggerType: 'PCN',
    subjectType: 'PART',
    subjectName: 'PN-000061',
    subjectSubtitle: 'Polyurethane topcoat, white',
    severity: 'MEDIUM',
    dueLabel: 'LTB 120d',
    revenueAtRisk: '$0.2M',
    bomsAffected: 3,
    linesAffected: 6,
    altsAvailable: 2,
    altsApproved: 0,
    sourceLabel: 'PCN-PPG-2026-0078',
    affectedBoms: [
      { bomName: 'BOM-Aircraft-Cabin', unitsPerQtr: 200, stockWeeks: 14, altStatus: 'CANDIDATE' },
      { bomName: 'BOM-Aircraft-Trim',  unitsPerQtr: 90,  stockWeeks: 9,  altStatus: 'CANDIDATE' },
    ],
  },
  {
    id: 'tr-005',
    triggerType: 'RFQ',
    subjectType: 'BOM',
    subjectName: 'BOM-Acme-RFQ-2026-04',
    subjectSubtitle: 'Acme Corp · 142 lines · target $84/unit',
    severity: 'HIGH',
    dueLabel: 'Due 3d',
    customer: 'Acme Corp',
    linesAffected: 142,
    altsAvailable: 8,
    altsApproved: 0,
    sourceLabel: 'RFQ-2026-04 · uploaded by sales',
    affectedLines: [
      { line: 14, partName: 'STM32F407VGT6', trigger: 'EOL',        altStatus: 'CANDIDATE' },
      { line: 22, partName: 'MLCC 0402 X7R', trigger: 'ALLOCATION', altStatus: 'CANDIDATE' },
      { line: 31, partName: 'AAA-XYZ-789',   trigger: 'PCN',        altStatus: 'NONE' },
    ],
  },
  {
    id: 'tr-006',
    triggerType: 'EOL',
    subjectType: 'PART',
    subjectName: 'PN-000291',
    subjectSubtitle: 'ELT 406 MHz with NAV interface',
    severity: 'LOW',
    dueLabel: 'LTB 180d',
    revenueAtRisk: '$0.1M',
    bomsAffected: 1,
    linesAffected: 1,
    altsAvailable: 1,
    altsApproved: 0,
    sourceLabel: 'PCN-Astronics-2026-0011',
    affectedBoms: [
      { bomName: 'BOM-Aircraft-Avionics', unitsPerQtr: 40, stockWeeks: 22, altStatus: 'CANDIDATE' },
    ],
  },
];

// ── Visual config per trigger ────────────────────────────────

const TRIGGER_CONFIG: Record<TriggerType, { label: string; color: string; bg: string }> = {
  PCN:          { label: 'PCN',          color: '#b45309', bg: '#fef3c7' },
  EOL:          { label: 'EOL',          color: '#b91c1c', bg: '#fee2e2' },
  ALLOCATION:   { label: 'Allocation',   color: '#9333ea', bg: '#f3e8ff' },
  OBSOLESCENCE: { label: 'Obsolescence', color: '#dc2626', bg: '#fee2e2' },
  RFQ:          { label: 'RFQ',          color: '#1d4ed8', bg: '#dbeafe' },
  NEW_BOM:      { label: 'New BOM',      color: '#047857', bg: '#d1fae5' },
  COST_DOWN:    { label: 'Cost-down',    color: '#0e7490', bg: '#cffafe' },
};

const SEVERITY_COLOR: Record<Severity, string> = {
  CRITICAL: '#dc2626',
  HIGH:     '#ea580c',
  MEDIUM:   '#ca8a04',
  LOW:      '#64748b',
};

const ALT_STATUS_LABEL: Record<AffectedBom['altStatus'], { label: string; color: string }> = {
  APPROVED:  { label: 'alt approved',  color: '#16a34a' },
  CANDIDATE: { label: 'alt candidate', color: '#ca8a04' },
  NONE:      { label: 'no alt',        color: '#dc2626' },
};

// ── Component ────────────────────────────────────────────────

const EngineeringInboxMockPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeTriggers, setActiveTriggers] = useState<Set<TriggerType>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('PART');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['tr-002']));
  const [search, setSearch] = useState('');

  const toggleTrigger = (t: TriggerType) => {
    setActiveTriggers((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Counts per trigger for the chip strip
  const counts = useMemo(() => {
    const out: Record<TriggerType, number> = {
      PCN: 0, EOL: 0, ALLOCATION: 0, OBSOLESCENCE: 0, RFQ: 0, NEW_BOM: 0, COST_DOWN: 0,
    };
    MOCK_ITEMS.forEach((i) => { out[i.triggerType] += 1; });
    return out;
  }, []);

  const filtered = useMemo(() => {
    return MOCK_ITEMS.filter((i) => {
      if (activeTriggers.size > 0 && !activeTriggers.has(i.triggerType)) return false;
      if (search && !`${i.subjectName} ${i.subjectSubtitle || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [activeTriggers, search]);

  // ── Render ─────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%' }}>
      {/* Preview banner */}
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.info.main, 0.08),
          border: '1px dashed',
          borderColor: alpha(theme.palette.info.main, 0.4),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.dark }}>
          DESIGN MOCK
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Engineering Worklist (Orchestrator) · trigger-aware, part-grouped · hardcoded data, not wired
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/engineering/queue')}
            sx={{ textTransform: 'none' }}
          >
            Back to current queue
          </Button>
        </Box>
      </Box>

      {/* Header Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Engineering Worklist
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One worklist across PCN · EOL · Allocation · RFQ · Obsolescence — grouped by affected part or BOM
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/buyer/worklist-mock')}
            sx={{ textTransform: 'none' }}
          >
            ⇆ Buyer Worklist
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/engineering/inbox-mock/detail')}
            sx={{ textTransform: 'none', color: theme.palette.info.main }}
          >
            View detail mock →
          </Button>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Trigger chip strip — replaces the KPI strip; counts + multi-select filter */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          p: 1.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: theme.palette.divider,
          bgcolor: 'background.paper',
        }}
      >
        <Chip
          label={`All  ${MOCK_ITEMS.length}`}
          size="small"
          variant={activeTriggers.size === 0 ? 'filled' : 'outlined'}
          color={activeTriggers.size === 0 ? 'primary' : 'default'}
          onClick={() => setActiveTriggers(new Set())}
          sx={{ height: 30, fontWeight: 600 }}
        />
        {(Object.keys(TRIGGER_CONFIG) as TriggerType[]).map((t) => {
          const cfg = TRIGGER_CONFIG[t];
          const active = activeTriggers.has(t);
          const c = counts[t];
          return (
            <Chip
              key={t}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.color }} />
                  <span>{cfg.label}</span>
                  <Box
                    sx={{
                      ml: 0.5,
                      px: 0.75,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: active ? alpha('#fff', 0.25) : cfg.bg,
                      color: active ? '#fff' : cfg.color,
                    }}
                  >
                    {c}
                  </Box>
                </Box>
              }
              size="small"
              variant={active ? 'filled' : 'outlined'}
              onClick={() => toggleTrigger(t)}
              sx={{
                height: 30,
                bgcolor: active ? cfg.color : 'transparent',
                color: active ? '#fff' : 'inherit',
                borderColor: active ? cfg.color : theme.palette.divider,
                '&:hover': { bgcolor: active ? cfg.color : alpha(cfg.color, 0.08) },
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          );
        })}
      </Box>

      {/* Toolbar: group-by + search */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mr: 0.5 }}>
          Group by
        </Typography>
        <ToggleButtonGroup
          value={groupBy}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setGroupBy(v); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.25 } }}
        >
          <ToggleButton value="PART">Part / Material</ToggleButton>
          <ToggleButton value="BOM">BOM</ToggleButton>
          <ToggleButton value="CUSTOMER">Customer</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search part, MPN, BOM…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
              </InputAdornment>
            ),
          }}
          sx={{ ml: 'auto', width: 280, '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.75 } }}
        />
        <Typography variant="caption" color="text.disabled">
          {filtered.length} items
        </Typography>
      </Stack>

      {/* Triage table — one row per affected Part/Material/BOM (collapses cross-refs) */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#f8fafc',
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', px: 1 }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: theme.palette.text.secondary,
                    py: 1.25,
                    border: 0,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableCell sx={{ pl: 1, width: 36 }} />
                <TableCell sx={{ pl: 1 }}>
                  {groupBy === 'PART' ? 'Master Part' : groupBy === 'BOM' ? 'Affected BOM' : 'Customer'}
                </TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">$ at risk</TableCell>
                <TableCell align="right">Where used</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell sx={{ pr: 2 }}>Source</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item) => {
                const cfg = TRIGGER_CONFIG[item.triggerType];
                const isExpanded = expanded.has(item.id);
                const sevColor = SEVERITY_COLOR[item.severity];
                return (
                  <React.Fragment key={item.id}>
                    {/* Main row */}
                    <TableRow
                      onClick={() => toggleExpand(item.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: '#fff',
                        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
                        '& td': { border: 0, py: 1.25 },
                        '&:hover td': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                        position: 'relative',
                      }}
                    >
                      <TableCell sx={{ pl: 1, width: 36, position: 'relative' }}>
                        {/* Severity stripe */}
                        <Box sx={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 1, bgcolor: sevColor }} />
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {isExpanded ? <ExpandIcon sx={{ fontSize: 18 }} /> : <ChevronIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ pl: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {item.subjectName}
                        </Typography>
                        {item.subjectSubtitle && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {item.subjectSubtitle}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: cfg.bg,
                            color: cfg.color,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <DotIcon sx={{ fontSize: 10, color: sevColor }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: sevColor, textTransform: 'capitalize' }}>
                            {item.severity.toLowerCase()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {item.dueLabel && (
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {item.dueLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          {item.revenueAtRisk || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          {item.bomsAffected != null && `${item.bomsAffected} BOM${item.bomsAffected !== 1 ? 's' : ''}`}
                          {item.bomsAffected != null && item.linesAffected != null && ' · '}
                          {item.linesAffected != null && `${item.linesAffected} line${item.linesAffected !== 1 ? 's' : ''}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.altsAvailable != null && (
                          <Typography variant="caption" color="text.secondary">
                            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {item.altsAvailable}
                            </Box>{' '}
                            alts{' · '}
                            <Box
                              component="span"
                              sx={{ fontWeight: 600, color: (item.altsApproved || 0) > 0 ? '#16a34a' : 'text.secondary' }}
                            >
                              {item.altsApproved ?? 0} approved
                            </Box>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ pr: 2 }}>
                        <Typography variant="caption" color="text.disabled">
                          {item.sourceLabel}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" title="Open resolution"><OpenIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" title="Snooze"><SnoozeIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" title="Assign"><AssignIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expanded row — affected BOMs (or affected lines if BOM-subject) */}
                    <TableRow sx={{ '& td': { border: 0, p: 0 } }}>
                      <TableCell colSpan={10} sx={{ p: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              mt: -1,
                              mb: 1,
                              ml: 5,
                              mr: 1,
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                              border: '1px solid',
                              borderColor: alpha(theme.palette.primary.main, 0.12),
                            }}
                          >
                            {item.affectedBoms && item.affectedBoms.length > 0 && (
                              <>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Affected BOMs ({item.affectedBoms.length}{item.bomsAffected && item.bomsAffected > item.affectedBoms.length ? ` of ${item.bomsAffected}` : ''})
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                  {item.affectedBoms.map((b) => {
                                    const alt = ALT_STATUS_LABEL[b.altStatus];
                                    return (
                                      <Box
                                        key={b.bomName}
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                          gap: 1,
                                          py: 0.5,
                                          alignItems: 'center',
                                          borderTop: '1px dashed',
                                          borderColor: alpha(theme.palette.primary.main, 0.12),
                                          fontSize: '0.8125rem',
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{b.bomName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{b.unitsPerQtr.toLocaleString()} units / qtr</Typography>
                                        <Typography variant="caption" color={b.stockWeeks <= 4 ? '#dc2626' : 'text.secondary'} sx={{ fontWeight: b.stockWeeks <= 4 ? 600 : 400 }}>
                                          {b.stockWeeks}w stock
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: alt.color, fontWeight: 600 }}>{alt.label}</Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </>
                            )}
                            {item.affectedLines && item.affectedLines.length > 0 && (
                              <>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Lines needing resolution ({item.affectedLines.length}{item.linesAffected ? ` of ${item.linesAffected}` : ''})
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                  {item.affectedLines.map((l) => {
                                    const alt = ALT_STATUS_LABEL[l.altStatus];
                                    const tcfg = TRIGGER_CONFIG[l.trigger];
                                    return (
                                      <Box
                                        key={l.line}
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: '60px 2fr 1fr 1fr',
                                          gap: 1,
                                          py: 0.5,
                                          alignItems: 'center',
                                          borderTop: '1px dashed',
                                          borderColor: alpha(theme.palette.primary.main, 0.12),
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Line {l.line}</Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{l.partName}</Typography>
                                        <Chip label={tcfg.label} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: tcfg.bg, color: tcfg.color, '& .MuiChip-label': { px: 0.75 } }} />
                                        <Typography variant="caption" sx={{ color: alt.color, fontWeight: 600 }}>{alt.label}</Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                              <Button size="small" variant="contained" sx={{ textTransform: 'none', boxShadow: 'none' }}>
                                Open resolution
                              </Button>
                              <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}>
                                Bulk swap
                              </Button>
                              <Button size="small" variant="text" sx={{ textTransform: 'none' }}>
                                Assign engineer
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footnote */}
      <Box sx={{ mt: 2, mb: 4, color: 'text.disabled' }}>
        <Typography variant="caption">
          What changed vs. current queue: row entity is the affected Part/Material (or BOM), not each cross-reference; trigger reason is a primary
          filter and badge; counts per trigger are visible up-front; alternates collapse into a “3 alts · 1 approved” chip and only expand on demand.
        </Typography>
      </Box>
    </Box>
  );
};

export default EngineeringInboxMockPage;
